/**
 * Karma Core — Universal Provider Client
 *
 * One unified interface across ALL providers.
 * The rest of the system never imports a specific SDK — it calls this.
 *
 * Text:    Claude (Anthropic) · GPT-4o (OpenAI) · Grok (xAI) · Gemini (Google) · Mistral · DeepSeek · Together
 * Image:   DALL·E 3 (OpenAI) · Stable Diffusion (Stability AI) · Flux (Replicate)
 * Video:   Kling / Wan / LTX (Replicate)
 * Audio:   ElevenLabs TTS · Whisper (OpenAI)
 * Embed:   text-embedding-3-small (OpenAI) · Gemini embed
 */

import type { Modality, ExecutionStrategy } from "./types";
import { routeRequest, recordProviderFailure, recordProviderSuccess, isCircuitBreakerOpen } from "./router";
// NOTE: this file is imported by client-side code too (e.g.
// lib/mindaloo/voiceIntentJudge.ts), so it must never import anything that
// touches next/headers or server-only secrets (like the Supabase admin
// client) — that breaks client bundling. Usage recording against
// lib/consciousness/serverBudget.ts happens in the server-only API routes
// that call callText(), not in here.

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ProviderMessage {
  role: "user" | "assistant" | "system";
  content: string;
}

export interface TextCallOptions {
  messages: ProviderMessage[];
  systemPrompt?: string;
  maxTokens?: number;
  temperature?: number;
  stream?: boolean;
  strategy?: ExecutionStrategy;
  /** Force a specific provider — bypasses router */
  forceProvider?: string;
  /** Restrict routing to these providers while keeping the normal ranking
   *  and failover between them. Unlike forceProvider this doesn't pin a
   *  single provider, so one provider having a bad moment doesn't fail the
   *  request. Ignored if none of them are configured. */
  allowedProviders?: readonly string[];
}

export interface TextCallResult {
  text: string;
  provider: string;
  model: string;
  inputTokens: number;
  outputTokens: number;
  latencyMs: number;
}

export interface ImageCallOptions {
  prompt: string;
  width?: number;
  height?: number;
  /** DALL-E 3 only supports these exact strings — takes priority over width/height on the OpenAI path. */
  size?: "1024x1024" | "1792x1024" | "1024x1792";
  /** DALL-E 3 only. */
  quality?: "standard" | "hd";
  /** DALL-E 3 only. */
  style?: "vivid" | "natural";
  /** "stability" | "openai" | "replicate-flux" */
  preferProvider?: string;
}

export interface ImageCallResult {
  url: string;
  provider: string;
  model: string;
}

export interface VideoCallOptions {
  prompt: string;
  durationSeconds?: number;
  /** "replicate-kling" | "replicate-wan" | "replicate-ltx" */
  preferProvider?: string;
}

export interface VideoCallResult {
  url: string;
  provider: string;
  model: string;
}

export interface AudioCallOptions {
  text: string;
  voiceId?: string;
  /** "elevenlabs" | "openai" */
  preferProvider?: string;
}

export interface AudioCallResult {
  url: string;
  provider: string;
}

export interface TranscribeOptions {
  /** Raw audio bytes (webm/mp4/wav/etc — whatever the recorder produced) */
  audio: Buffer;
  /** File extension/mime hint so Whisper knows how to decode it, e.g. "webm", "mp4", "wav" */
  filename: string;
  language?: string;
}

export interface TranscribeResult {
  text: string;
  provider: string;
}

// ─── Timeouts ─────────────────────────────────────────────────────────────────
// None of these calls had any request timeout — confirmed live, one provider
// call hung for 160+ seconds with no error, blocking the whole voice turn.
// On a flaky (e.g. mobile) network a stalled connection with no timeout just
// sits there instead of failing fast and letting the fallback chain move to
// the next provider. Text/judge calls need to stay snappy for voice; media
// generation (image/video) legitimately takes longer.
const TEXT_TIMEOUT_MS = 20_000;
const MEDIA_TIMEOUT_MS = 60_000;

// ─── Env helpers ─────────────────────────────────────────────────────────────

const env = () => process.env;

const has = (key: string) => !!env()[key];

export function getAvailableProviders(): string[] {
  const available: string[] = [];
  if (has("ANTHROPIC_API_KEY")) available.push("anthropic");
  if (has("OPENAI_API_KEY")) available.push("openai");
  if (has("XAI_API_KEY")) available.push("xai");
  if (has("GOOGLE_GEMINI_API_KEY")) available.push("gemini");
  if (has("DEEPSEEK_API_KEY")) available.push("deepseek");
  if (has("TOGETHER_API_KEY")) available.push("together");
  if (has("COHERE_API_KEY")) available.push("cohere");
  return available;
}

// ─── TEXT ─────────────────────────────────────────────────────────────────────

async function callAnthropic(opts: TextCallOptions): Promise<TextCallResult> {
  const { default: Anthropic } = await import("@anthropic-ai/sdk");
  const client = new Anthropic({ apiKey: env().ANTHROPIC_API_KEY });

  const model = opts.strategy === "FAST" ? "claude-haiku-4-5"
    : opts.strategy === "DEEP" ? "claude-opus-4-5"
    : "claude-sonnet-4-5";

  const t0 = Date.now();
  const msgs = opts.messages.filter(m => m.role !== "system");
  const res = await client.messages.create({
    model,
    system: opts.systemPrompt ?? opts.messages.find(m => m.role === "system")?.content,
    messages: msgs as any,
    max_tokens: opts.maxTokens ?? 4096,
  }, { timeout: TEXT_TIMEOUT_MS });
  const text = (res.content.find((b: any) => b.type === "text") as any)?.text ?? "";
  return {
    text, provider: "anthropic", model,
    inputTokens: res.usage.input_tokens,
    outputTokens: res.usage.output_tokens,
    latencyMs: Date.now() - t0,
  };
}

async function callOpenAI(opts: TextCallOptions): Promise<TextCallResult> {
  const { default: OpenAI } = await import("openai");
  const client = new OpenAI({ apiKey: env().OPENAI_API_KEY });

  const model = opts.strategy === "FAST" ? "gpt-4o-mini"
    : opts.strategy === "DEEP" ? "gpt-4o"
    : "gpt-4o";

  const t0 = Date.now();
  const messages: any[] = opts.systemPrompt
    ? [{ role: "system", content: opts.systemPrompt }, ...opts.messages.filter(m => m.role !== "system")]
    : opts.messages;

  const res = await client.chat.completions.create({
    model,
    messages,
    max_tokens: opts.maxTokens ?? 4096,
    temperature: opts.temperature ?? 0.7,
  }, { timeout: TEXT_TIMEOUT_MS });
  const text = res.choices[0]?.message?.content ?? "";
  return {
    text, provider: "openai", model,
    inputTokens: res.usage?.prompt_tokens ?? 0,
    outputTokens: res.usage?.completion_tokens ?? 0,
    latencyMs: Date.now() - t0,
  };
}

async function callGrok(opts: TextCallOptions): Promise<TextCallResult> {
  const { default: OpenAI } = await import("openai");
  const client = new OpenAI({
    apiKey: env().XAI_API_KEY,
    baseURL: "https://api.x.ai/v1",
  });

  // "grok-3-latest" isn't a real xAI model name — xAI's own lineup skips
  // straight from 2 to 4, so this 404'd on every call. grok-4-latest is
  // the current flagship (see the same fix in lib/ai/providers/xai.ts).
  const model = "grok-4-latest";
  const t0 = Date.now();
  const messages: any[] = opts.systemPrompt
    ? [{ role: "system", content: opts.systemPrompt }, ...opts.messages.filter(m => m.role !== "system")]
    : opts.messages;

  const res = await client.chat.completions.create({
    model,
    messages,
    max_tokens: opts.maxTokens ?? 4096,
    temperature: opts.temperature ?? 0.7,
  }, { timeout: TEXT_TIMEOUT_MS });
  const text = res.choices[0]?.message?.content ?? "";
  return {
    text, provider: "xai", model,
    inputTokens: res.usage?.prompt_tokens ?? 0,
    outputTokens: res.usage?.completion_tokens ?? 0,
    latencyMs: Date.now() - t0,
  };
}

async function callGemini(opts: TextCallOptions): Promise<TextCallResult> {
  // Pinned dated snapshots (gemini-2.0-flash, gemini-2.5-flash, gemini-2.5-pro)
  // get deprecated out from under this app without warning — confirmed live:
  // gemini-2.5-flash now 404s for this key with "no longer available to new
  // users," which every NORMAL-strategy call was silently eating ~3s on
  // before falling through to the next provider. The "-latest" aliases are
  // Google's own answer to this — they keep pointing at the current
  // recommended model instead of a snapshot that eventually gets sunset.
  const model = opts.strategy === "DEEP" ? "gemini-pro-latest" : "gemini-flash-latest";

  const t0 = Date.now();
  const userMessages = opts.messages.filter(m => m.role !== "system");
  const sysInstruction = opts.systemPrompt ?? opts.messages.find(m => m.role === "system")?.content ?? "";

  const contents = userMessages.map(m => ({
    role: m.role === "assistant" ? "model" : "user",
    parts: [{ text: m.content }],
  }));

  const body: any = { contents };
  if (sysInstruction) {
    body.systemInstruction = { parts: [{ text: sysInstruction }] };
  }
  if (opts.maxTokens) {
    body.generationConfig = { maxOutputTokens: opts.maxTokens, temperature: opts.temperature ?? 0.7 };
    // Gemini 2.5 models "think" internally by default, and those thinking
    // tokens are deducted from maxOutputTokens — with a modest budget (the
    // voice pilot passes 1024) and a long system prompt, the model can burn
    // the entire budget thinking and return a completely empty `text` with
    // no error, which is exactly what was happening. FAST/NORMAL strategies
    // are for quick conversational replies, not deep reasoning, so disable
    // thinking outright rather than just hoping the budget is large enough.
    if (opts.strategy !== "DEEP") {
      body.generationConfig.thinkingConfig = { thinkingBudget: 0 };
    }
  }

  const res = await fetch(
    `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${env().GOOGLE_GEMINI_API_KEY}`,
    { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body), signal: AbortSignal.timeout(TEXT_TIMEOUT_MS) }
  );
  const data = await res.json();

  // The response status was never checked here — an error response (bad
  // API key, quota, or an invalid request field) has no `candidates` at
  // all, so it silently fell through to an empty string instead of
  // throwing. That's what actually broke the voice pilot: not a token
  // budget, an unchecked HTTP error being treated as a successful empty
  // reply. Fail loudly instead, with Google's real error message.
  if (!res.ok) {
    console.error("[gemini] API error", { status: res.status, model, error: data?.error });
    throw new Error(data?.error?.message || `Gemini API error: ${res.status}`);
  }

  const text = data?.candidates?.[0]?.content?.parts?.[0]?.text ?? "";
  if (!text) {
    console.warn("[gemini] empty text response", {
      model,
      finishReason: data?.candidates?.[0]?.finishReason,
      promptFeedback: data?.promptFeedback,
      usageMetadata: data?.usageMetadata,
      hadThinkingBudgetZero: body.generationConfig?.thinkingConfig?.thinkingBudget === 0,
    });
  }
  return {
    text, provider: "gemini", model,
    inputTokens: data?.usageMetadata?.promptTokenCount ?? 0,
    outputTokens: data?.usageMetadata?.candidatesTokenCount ?? 0,
    latencyMs: Date.now() - t0,
  };
}

async function callDeepSeek(opts: TextCallOptions): Promise<TextCallResult> {
  const { default: OpenAI } = await import("openai");
  const client = new OpenAI({
    apiKey: env().DEEPSEEK_API_KEY,
    baseURL: "https://api.deepseek.com",
  });

  const model = opts.strategy === "DEEP" ? "deepseek-reasoner" : "deepseek-chat";
  const t0 = Date.now();
  const messages: any[] = opts.systemPrompt
    ? [{ role: "system", content: opts.systemPrompt }, ...opts.messages.filter(m => m.role !== "system")]
    : opts.messages;

  const res = await client.chat.completions.create({
    model,
    messages,
    max_tokens: opts.maxTokens ?? 4096,
  }, { timeout: TEXT_TIMEOUT_MS });
  const text = res.choices[0]?.message?.content ?? "";
  return {
    text, provider: "deepseek", model,
    inputTokens: res.usage?.prompt_tokens ?? 0,
    outputTokens: res.usage?.completion_tokens ?? 0,
    latencyMs: Date.now() - t0,
  };
}

async function callTogether(opts: TextCallOptions): Promise<TextCallResult> {
  const model = "meta-llama/Llama-3.3-70B-Instruct-Turbo";
  const t0 = Date.now();
  const messages: any[] = opts.systemPrompt
    ? [{ role: "system", content: opts.systemPrompt }, ...opts.messages.filter(m => m.role !== "system")]
    : opts.messages;

  const res = await fetch("https://api.together.xyz/v1/chat/completions", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${env().TOGETHER_API_KEY}` },
    body: JSON.stringify({ model, messages, max_tokens: opts.maxTokens ?? 4096 }),
    signal: AbortSignal.timeout(TEXT_TIMEOUT_MS),
  });
  const data = await res.json();
  const text = data?.choices?.[0]?.message?.content ?? "";
  return {
    text, provider: "together", model,
    inputTokens: data?.usage?.prompt_tokens ?? 0,
    outputTokens: data?.usage?.completion_tokens ?? 0,
    latencyMs: Date.now() - t0,
  };
}

// ─── IMAGE ────────────────────────────────────────────────────────────────────

async function generateImageStability(opts: ImageCallOptions): Promise<ImageCallResult> {
  const res = await fetch(
    "https://api.stability.ai/v2beta/stable-image/generate/core",
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${env().STABILITY_API_KEY}`,
        Accept: "application/json",
      },
      body: (() => {
        const fd = new FormData();
        fd.append("prompt", opts.prompt);
        fd.append("output_format", "webp");
        if (opts.width) fd.append("width", String(opts.width));
        if (opts.height) fd.append("height", String(opts.height));
        return fd;
      })(),
      signal: AbortSignal.timeout(MEDIA_TIMEOUT_MS),
    }
  );
  const data = await res.json();
  if (data.image) {
    return { url: `data:image/webp;base64,${data.image}`, provider: "stability", model: "stable-image-core" };
  }
  throw new Error(data.message ?? "Stability image generation failed");
}

async function generateImageOpenAI(opts: ImageCallOptions): Promise<ImageCallResult> {
  const { default: OpenAI } = await import("openai");
  const client = new OpenAI({ apiKey: env().OPENAI_API_KEY });
  const res = await client.images.generate({
    model: "dall-e-3",
    prompt: opts.prompt,
    n: 1,
    size: opts.size ?? "1024x1024",
    quality: opts.quality ?? "standard",
    style: opts.style ?? "vivid",
    response_format: "url",
  }, { timeout: MEDIA_TIMEOUT_MS });
  const url = res.data?.[0]?.url;
  if (!url) throw new Error("DALL-E 3 returned an empty image URL");
  // DALL-E's URL expires (~1hr) — the app persists this in chat history/share
  // links as if permanent, so a stale URL silently fails to load later ("says
  // generated but not visible"). Fetch it now while it's fresh and inline it,
  // matching how the Stability provider already behaves below.
  return { url: await inlineImageUrl(url), provider: "openai", model: "dall-e-3" };
}

/** Downloads a (possibly temporary) image URL and returns it as a permanent base64 data URI. */
async function inlineImageUrl(url: string): Promise<string> {
  const res = await fetch(url, { signal: AbortSignal.timeout(MEDIA_TIMEOUT_MS) });
  if (!res.ok) throw new Error(`Failed to download generated image: ${res.status}`);
  const buffer = Buffer.from(await res.arrayBuffer());
  const contentType = res.headers.get("content-type") || "image/png";
  return `data:${contentType};base64,${buffer.toString("base64")}`;
}

async function generateImageReplicate(opts: ImageCallOptions): Promise<ImageCallResult> {
  const res = await fetch("https://api.replicate.com/v1/models/black-forest-labs/flux-1.1-pro/predictions", {
    method: "POST",
    headers: {
      Authorization: `Bearer ${env().REPLICATE_API_TOKEN}`,
      "Content-Type": "application/json",
      Prefer: "wait",
    },
    body: JSON.stringify({ input: { prompt: opts.prompt, width: opts.width ?? 1024, height: opts.height ?? 1024 } }),
    signal: AbortSignal.timeout(MEDIA_TIMEOUT_MS),
  });
  const data = await res.json();
  if (!res.ok) {
    const detail = data.detail || data.message || JSON.stringify(data);
    if (res.status === 401 || res.status === 403) {
      throw new Error(`REPLICATE_API_TOKEN authentication failed: ${detail}`);
    }
    throw new Error(`Replicate image request failed (${res.status}): ${detail}`);
  }
  const url = Array.isArray(data.output) ? data.output[0] : data.output;
  if (!url) throw new Error("Replicate image generation returned an empty URL");
  // Same expiring-URL issue as OpenAI above — inline it while it's fresh.
  return { url: await inlineImageUrl(url), provider: "replicate", model: "flux-1.1-pro" };
}

// ─── VIDEO ────────────────────────────────────────────────────────────────────

async function generateVideoReplicate(opts: VideoCallOptions): Promise<VideoCallResult> {
  // Try Wan2.1 (fast, high quality)
  const model = "wavespeedai/wan-2.1-t2v-480p";
  const startRes = await fetch(`https://api.replicate.com/v1/models/${model}/predictions`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${env().REPLICATE_API_TOKEN}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      input: {
        prompt: opts.prompt,
        num_frames: Math.min((opts.durationSeconds ?? 4) * 16, 81),
      },
    }),
  });
  const startData = await startRes.json();
  if (!startRes.ok) {
    const detail = startData.detail || startData.message || JSON.stringify(startData);
    if (startRes.status === 401 || startRes.status === 403) {
      throw new Error(`REPLICATE_API_TOKEN authentication failed: ${detail}`);
    }
    throw new Error(`Replicate video request failed (${startRes.status}): ${detail}`);
  }
  const predictionId = startData.id;
  if (!predictionId) throw new Error(`Replicate video: no prediction ID. Response: ${JSON.stringify(startData)}`);

  // Poll until done (max 3 mins)
  const deadline = Date.now() + 180_000;
  while (Date.now() < deadline) {
    await new Promise(r => setTimeout(r, 4000));
    const pollRes = await fetch(`https://api.replicate.com/v1/predictions/${predictionId}`, {
      headers: { Authorization: `Bearer ${env().REPLICATE_API_TOKEN}` },
    });
    const poll = await pollRes.json();
    if (poll.status === "succeeded") {
      const url = Array.isArray(poll.output) ? poll.output[0] : poll.output;
      if (!url) throw new Error("Replicate video completed but returned an empty URL");
      return { url, provider: "replicate", model };
    }
    if (poll.status === "failed") throw new Error(`Video generation failed: ${poll.error}`);
  }
  throw new Error("Video generation timed out");
}

// ─── AUDIO ────────────────────────────────────────────────────────────────────

async function generateAudioElevenLabs(opts: AudioCallOptions): Promise<AudioCallResult> {
  const voiceId = opts.voiceId ?? "EXAVITQu4vr4xnSDxMaL"; // Sarah — natural, warm
  const res = await fetch(`https://api.elevenlabs.io/v1/text-to-speech/${voiceId}`, {
    method: "POST",
    headers: {
      "xi-api-key": env().ELEVENLABS_API_KEY!,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      text: opts.text,
      model_id: "eleven_turbo_v2_5",
      voice_settings: { stability: 0.5, similarity_boost: 0.75 },
    }),
    signal: AbortSignal.timeout(TEXT_TIMEOUT_MS),
  });
  if (!res.ok) throw new Error(`ElevenLabs error: ${res.status}`);
  const buffer = await res.arrayBuffer();
  const base64 = Buffer.from(buffer).toString("base64");
  return { url: `data:audio/mpeg;base64,${base64}`, provider: "elevenlabs" };
}

async function generateAudioOpenAI(opts: AudioCallOptions): Promise<AudioCallResult> {
  const { default: OpenAI } = await import("openai");
  const client = new OpenAI({ apiKey: env().OPENAI_API_KEY });
  const res = await client.audio.speech.create({
    model: "tts-1",
    voice: "nova",
    input: opts.text,
  }, { timeout: TEXT_TIMEOUT_MS });
  const buffer = await res.arrayBuffer();
  const base64 = Buffer.from(buffer).toString("base64");
  return { url: `data:audio/mpeg;base64,${base64}`, provider: "openai" };
}

/**
 * transcribeAudio — Whisper (OpenAI). Used by the recorded-audio fallback for
 * browsers (iOS Safari/WebKit) that don't implement the native
 * SpeechRecognition API at all.
 */
async function transcribeAudioOpenAI(opts: TranscribeOptions): Promise<TranscribeResult> {
  const { default: OpenAI } = await import("openai");
  const { toFile } = await import("openai/uploads");
  const client = new OpenAI({ apiKey: env().OPENAI_API_KEY });
  const file = await toFile(opts.audio, opts.filename);
  const res = await client.audio.transcriptions.create({
    model: "whisper-1",
    file,
    language: opts.language,
  }, { timeout: TEXT_TIMEOUT_MS });
  return { text: res.text, provider: "openai" };
}

async function transcribeAudioElevenLabs(opts: TranscribeOptions): Promise<TranscribeResult> {
  const form = new FormData();
  form.append("model_id", "scribe_v1");
  form.append("file", new Blob([new Uint8Array(opts.audio)]), opts.filename);
  const res = await fetch("https://api.elevenlabs.io/v1/speech-to-text", {
    method: "POST",
    headers: { "xi-api-key": env().ELEVENLABS_API_KEY! },
    body: form,
    signal: AbortSignal.timeout(TEXT_TIMEOUT_MS),
  });
  if (!res.ok) throw new Error(`ElevenLabs transcription error: ${res.status} ${await res.text().catch(() => "")}`);
  const data = await res.json();
  return { text: data.text ?? "", provider: "elevenlabs" };
}

/**
 * Voice input's server-side fallback for browsers with no native
 * SpeechRecognition (iOS Safari/WebKit — see components/chat/VoiceInputButton.tsx).
 * Used to be OpenAI-only with no fallback at all: if that one provider is
 * out of quota (the exact condition hit repeatedly this session), voice
 * input on Safari failed outright with nothing else to try. ElevenLabs
 * Scribe is a second, independent provider for the same job.
 */
export async function transcribeAudio(opts: TranscribeOptions): Promise<TranscribeResult> {
  const attempts: Array<() => Promise<TranscribeResult>> = [];
  if (has("OPENAI_API_KEY")) attempts.push(() => transcribeAudioOpenAI(opts));
  if (has("ELEVENLABS_API_KEY")) attempts.push(() => transcribeAudioElevenLabs(opts));
  if (attempts.length === 0) throw new Error("No transcription provider configured (need OPENAI_API_KEY or ELEVENLABS_API_KEY)");

  const errors: string[] = [];
  for (const attempt of attempts) {
    try {
      return await attempt();
    } catch (err: any) {
      errors.push(err?.message || String(err));
    }
  }
  throw new Error(`All transcription providers failed: ${errors.join(" | ")}`);
}

// ─── ROUTER DISPATCH ─────────────────────────────────────────────────────────

const TEXT_PROVIDERS: Record<string, (o: TextCallOptions) => Promise<TextCallResult>> = {
  anthropic: callAnthropic,
  openai: callOpenAI,
  xai: callGrok,
  gemini: callGemini,
  deepseek: callDeepSeek,
  together: callTogether,
};

/**
 * callText — routes to the best available provider automatically.
 * Falls back through the ranked list until one succeeds.
 */
export async function callText(opts: TextCallOptions): Promise<TextCallResult> {
  if (opts.forceProvider && TEXT_PROVIDERS[opts.forceProvider]) {
    return TEXT_PROVIDERS[opts.forceProvider](opts);
  }

  const allConfigured = getAvailableProviders();
  // allowedProviders narrows the pool but never empties it: if none of the
  // allowed providers have keys, route normally instead of failing outright.
  const narrowed = opts.allowedProviders
    ? allConfigured.filter(p => opts.allowedProviders!.includes(p))
    : allConfigured;
  const available = narrowed.length > 0 ? narrowed : allConfigured;
  const ranked = routeRequest({
    messages: opts.messages,
    strategy: opts.strategy ?? "NORMAL",
    modality: "text",
  }).filter(r => available.includes(r.provider));

  // routeRequest() already excludes circuit-broken providers via
  // calculateScore(), but when `ranked` comes back empty (e.g. every
  // provider is currently open) this used to fall back to the raw,
  // unfiltered `available` list — silently undoing that protection and
  // retrying providers already known to be dead. Filtering here too, and
  // falling back to the FULL list only as a last resort if literally
  // everything is circuit-broken, so a request still gets attempted
  // rather than failing immediately.
  const healthyAvailable = available.filter(p => !isCircuitBreakerOpen(p));
  const fallbackList = healthyAvailable.length > 0 ? healthyAvailable : available;
  const chain = ranked.length > 0 ? ranked.map(r => r.provider) : fallbackList;
  const errors: string[] = [];

  for (const provider of chain) {
    const fn = TEXT_PROVIDERS[provider];
    if (!fn) continue;
    try {
      const result = await fn(opts);
      recordProviderSuccess(provider);
      return result;
    } catch (err: any) {
      errors.push(`${provider}: ${err?.message}`);
      recordProviderFailure(provider, err?.message);
      console.warn(`[karma-router] ${provider} failed, trying next`, err?.message);
    }
  }

  throw new Error(`All providers failed: ${errors.join(" | ")}`);
}

/**
 * callImage — routes to best image provider with fallback.
 */
export async function callImage(opts: ImageCallOptions): Promise<ImageCallResult> {
  const prefer = opts.preferProvider;
  const chain: Array<() => Promise<ImageCallResult>> = [];

  if (prefer === "openai" && has("OPENAI_API_KEY")) chain.push(() => generateImageOpenAI(opts));
  if (prefer === "replicate" && has("REPLICATE_API_TOKEN")) chain.push(() => generateImageReplicate(opts));
  if (prefer === "stability" && has("STABILITY_API_KEY")) chain.push(() => generateImageStability(opts));

  // Default priority: Stability → Flux/Replicate → DALL·E 3
  if (!prefer || chain.length === 0) {
    if (has("STABILITY_API_KEY")) chain.push(() => generateImageStability(opts));
    if (has("REPLICATE_API_TOKEN")) chain.push(() => generateImageReplicate(opts));
    if (has("OPENAI_API_KEY")) chain.push(() => generateImageOpenAI(opts));
  }

  for (const fn of chain) {
    try { return await fn(); } catch (e) { console.warn("[karma-router] image fallback", e); }
  }
  throw new Error("No image provider succeeded");
}

/**
 * callVideo — Replicate is currently the only option (Kling, Wan, LTX).
 */
export async function callVideo(opts: VideoCallOptions): Promise<VideoCallResult> {
  if (!has("REPLICATE_API_TOKEN")) throw new Error("REPLICATE_API_TOKEN not set — video generation unavailable");
  return generateVideoReplicate(opts);
}

/**
 * callAudio — ElevenLabs preferred, falls back to OpenAI TTS.
 */
export async function callAudio(opts: AudioCallOptions): Promise<AudioCallResult> {
  if (opts.preferProvider === "openai" && has("OPENAI_API_KEY")) return generateAudioOpenAI(opts);
  if (has("ELEVENLABS_API_KEY")) {
    try { return await generateAudioElevenLabs(opts); } catch {}
  }
  if (has("OPENAI_API_KEY")) return generateAudioOpenAI(opts);
  throw new Error("No audio provider available");
}

/**
 * makeLlmCall — returns a LlmCall compatible with karma-core internal modules.
 * Modules like MemoryEvolutionSystem, ReflectionManager etc. use this.
 */
export interface LlmCallOptions {
  /** Restrict these internal calls to a set of providers (keeps ranking and
   *  failover among them). */
  allowedProviders?: readonly string[];
  /** Called with the real usage of each call. makeLlmCall only returns text,
   *  so without this every token these modules spent was thrown away —
   *  unrecorded against any budget. */
  onUsage?: (usage: { provider: string; model: string; inputTokens: number; outputTokens: number }) => void;
}

export function makeLlmCall(
  strategy: ExecutionStrategy = "FAST",
  opts: LlmCallOptions = {}
): import("./types").LlmCall {
  return async (prompt, options) => {
    const result = await callText({
      messages: [{ role: "user", content: prompt }],
      maxTokens: options?.maxTokens ?? 1024,
      temperature: options?.temperature ?? 0.7,
      strategy,
      allowedProviders: opts.allowedProviders,
    });
    try {
      opts.onUsage?.({
        provider: result.provider,
        model: result.model,
        inputTokens: result.inputTokens,
        outputTokens: result.outputTokens,
      });
    } catch {
      // Accounting must never fail the call it's accounting for.
    }
    return result.text;
  };
}
